import { useSyncExternalStore } from 'react';
import { Navigate } from 'react-router-dom';
import { VIEW_HOME } from './EsConstants';

/**
 * Talks, for whoever esback says may see it (Tim, 2026-10-30: nobody outside switchEsTalks may
 * learn the pages exist). The Changelog and the Devlog are not here: they left the flag on
 * 2026-10-31 (Tim: "just hide talks"), so their links are in the main bundle for everyone.
 *
 * <p>So the browser never reads that flag: it is not client-side in LaunchDarkly, and esback
 * evaluates it and sets the X-Es-More header on /authUser, signed out included, only when it is
 * on (HDR_AUTH_MORE). EsAuth hands the answer here. Everything that names the pages -- their
 * routes, components, paths, the footer's two links, the admin tile -- is in the
 * lazy chunk views/more/EsMoreChunk.js, which nothing loads unless the answer is yes, so the
 * main bundle carries none of it.
 *
 * <p>The routes join the router through patchRoutesOnNavigation (esMorePatch), which React
 * Router calls on any navigation whose match carries params, or that matched nothing. It returns
 * at once for a page this bundle has, and only for an unknown path waits for /authUser's first
 * answer and loads the chunk.
 */

let gOn = null;
const gListeners = new Set();
let gResolve;
let gFirst = new Promise( ( pResolve ) => { gResolve = pResolve; } );
let gPatched = false;

/** EsAuth's answer from /authUser: the header was there or it was not. */
export function esMoreSet( pOn ) {
    const aOn = pOn === true;
    const aChanged = gOn !== aOn;
    gOn = aOn;
    gResolve( aOn );
    if ( aChanged ) gListeners.forEach( ( pListener ) => pListener() );
}

const subscribe = ( pListener ) => {
    gListeners.add( pListener );
    return () => gListeners.delete( pListener );
};

/** True once esback has said yes; false before it has answered, and after a no. */
export const useEsMore = () => useSyncExternalStore( subscribe, () => gOn === true, () => false );

/** Whether esback has answered at all. */
export const useEsMoreKnown = () => useSyncExternalStore( subscribe, () => gOn !== null, () => false );

/** Inside the chunk's routes: home once the answer turns no, as after a sign-out. */
export function EsMoreGate( { children } ) {
    const aKnown = useEsMoreKnown();
    const aOn = useEsMore();
    if ( !aKnown ) return null;
    if ( !aOn ) return <Navigate to={ VIEW_HOME } replace />;
    return children;
}

export const esMoreChunk = () => import( '../views/more/EsMoreChunk' );

/** createBrowserRouter's patchRoutesOnNavigation; the root route's id is ROOT_ROUTE_ID. */
export const ROOT_ROUTE_ID = 'root';

// A loop, not /\/+$/: an anchored repeat backtracks over a long run of slashes.
const bare = ( pPath ) => {
    let aPath = String( pPath ?? '' );
    while ( aPath.endsWith( '/' ) ) aPath = aPath.slice( 0, -1 );
    return aPath;
};

export async function esMorePatch( { path, matches, patch } ) {
    if ( gPatched ) return;
    // A match that reaches the whole path through a route other than "*" is a page this bundle
    // has -- a :key route, say -- so there is nothing to find and nothing to wait for. An unknown
    // path arrives as a partial match that stops short of it (at the root, or its "/" child).
    const aLast = matches?.length ? matches[ matches.length - 1 ] : null;
    if ( aLast && aLast.route.path !== '*' && bare( aLast.pathname ) === bare( path ) ) return;

    await gFirst;
    if ( gOn !== true || gPatched ) return;

    const { moreRoutes } = await esMoreChunk();
    if ( gPatched ) return;
    gPatched = true;
    patch( ROOT_ROUTE_ID, moreRoutes() );
}

/** For tests: back to before esback answered. */
export function esMoreReset() {
    gOn = null;
    gPatched = false;
    gFirst = new Promise( ( pResolve ) => { gResolve = pResolve; } );
    gListeners.forEach( ( pListener ) => pListener() );
}
